<template>
  <div class="trending-players-section text-gray-900 dark:text-white rounded-lg min-h-[600px]">
    <!-- Section Title -->
    <div class="flex items-center gap-3 mb-[47px]">
      <div class="w-[7px] h-[23px] bg-red-600"></div>
      <h2
        class="font-sans font-extrabold not-italic text-sm leading-none tracking-widest uppercase"
      >
        Trending Players
      </h2>
    </div>

    <!-- Combined Tab Selection - Sport and Metric side by side -->
    <div class="flex items-center justify-between mb-6">
      <!-- Sport Selection Tabs (left aligned) -->
      <div class="relative">
        <div class="flex">
          <button
            v-for="sport in sports"
            :key="sport"
            @click="selectedSport = sport"
            :disabled="isLoading"
            :class="[
              'py-[7px] px-[11px] transition-all duration-300 ease-in-out font-sans font-normal text-base leading-tight tracking-normal',
              selectedSport === sport
                ? 'text-gray-900 dark:text-white font-bold'
                : 'text-gray-500 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-white',
              isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ]"
          >
            {{ sport }}
          </button>
        </div>
        <!-- Continuous border line for sport tabs -->
        <div class="absolute bottom-0 left-0 right-0 h-1 bg-gray-300 dark:bg-[#303947]">
          <div
            v-for="(sport, index) in sports"
            :key="sport"
            :class="[
              'absolute top-0 h-1 transition-all duration-300 ease-in-out',
              selectedSport === sport
                ? 'bg-gray-900 dark:bg-white'
                : 'bg-gray-300 dark:bg-[#303947]',
            ]"
            :style="{
              left: `${index * (100 / sports.length)}%`,
              width: `${100 / sports.length}%`,
            }"
          ></div>
        </div>
      </div>

      <!-- Metric Selection Tabs (right aligned) -->
      <div class="relative">
        <div class="flex">
          <button
            v-for="metric in metrics"
            :key="metric"
            @click="selectedMetric = metric"
            :disabled="isLoading"
            :class="[
              'py-[7px] px-[11px] transition-all duration-300 ease-in-out font-sans font-normal text-base leading-tight tracking-normal',
              selectedMetric === metric
                ? 'text-gray-900 dark:text-white font-bold'
                : 'text-gray-500 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-white',
              isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ]"
          >
            {{ metric }}
          </button>
        </div>
        <!-- Continuous border line for metric tabs -->
        <div class="absolute bottom-0 left-0 right-0 h-1 bg-gray-300 dark:bg-[#303947]">
          <div
            v-for="(metric, index) in metrics"
            :key="metric"
            :class="[
              'absolute top-0 h-1 transition-all duration-300 ease-in-out',
              selectedMetric === metric
                ? 'bg-gray-900 dark:bg-white'
                : 'bg-gray-300 dark:bg-[#303947]',
            ]"
            :style="{
              left: `${index * (100 / metrics.length)}%`,
              width: `${100 / metrics.length}%`,
            }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Players Table -->
    <div
      ref="tableContainer"
      class="trending-players-table overflow-y-auto relative h-[600px] mr-[-4px]"
    >
      <table class="w-full">
        <thead>
          <tr class="border-b-[3px] border-[#525252]">
            <th
              class="font-sans font-normal text-base leading-tight tracking-normal text-left py-3 px-2 pb-[7px] text-gray-900 dark:text-white w-12"
            >
              #
            </th>
            <th
              class="font-sans font-normal text-base leading-tight tracking-normal text-left py-3 px-2 pb-[7px] text-gray-900 dark:text-white"
            >
              Player
            </th>
            <th
              class="font-sans font-normal text-base leading-tight tracking-normal text-right py-3 px-2 pb-[7px] text-gray-900 dark:text-white w-20"
            >
              {{ metricDisplayName }}
            </th>
          </tr>
        </thead>
        <tbody class="relative">
          <!-- Loading State with Placeholder Rows -->
          <template v-if="isLoading">
            <tr
              v-for="i in props.limit"
              :key="i"
              class="hover:bg-neutral-100 dark:hover:bg-neutral-800/50 transition-colors duration-200"
            >
              <td class="px-2 pt-[1px] pb-[2px] w-12" :class="{ 'pt-[7px]': i === 1 }">
                <div class="h-4 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse"></div>
              </td>
              <td class="px-2 pt-[1px] pb-[2px]" :class="{ 'pt-[7px]': i === 1 }">
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 bg-neutral-200 dark:bg-neutral-800 rounded-full animate-pulse flex-shrink-0"
                  ></div>
                  <div
                    class="h-4 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse w-32"
                  ></div>
                </div>
              </td>
              <td class="px-2 pt-[1px] pb-[2px] text-right w-20" :class="{ 'pt-[7px]': i === 1 }">
                <div
                  class="h-4 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse w-16 inline-block"
                ></div>
              </td>
            </tr>
          </template>

          <!-- Data Rows (when loaded) -->
          <template v-else>
            <tr
              v-for="(player, index) in players"
              :key="player.id"
              class="hover:bg-neutral-100 dark:hover:bg-neutral-800/50 transition-colors duration-200"
            >
              <td
                class="font-sans font-normal text-base leading-tight tracking-normal px-2 pt-[1px] pb-[2px] text-gray-600 dark:text-neutral-300 w-12"
                :class="{ 'pt-[7px]': index === 0 }"
              >
                {{ player.rank }}
              </td>
              <td class="px-2 pt-[1px] pb-[2px]" :class="{ 'pt-[7px]': index === 0 }">
                <div class="flex items-center gap-3">
                  <!-- Team Logo or Initial -->
                  <NuxtLink
                    :to="`/teams/${player.teamId}`"
                    class="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 hover:opacity-80 transition-opacity"
                  >
                    <img
                      v-if="player.teamLogoUrl"
                      :src="player.teamLogoUrl"
                      :alt="player.teamName"
                      class="w-full h-full object-contain"
                      @error="handleImageError"
                    />
                    <div
                      v-else
                      class="w-full h-full bg-neutral-300 dark:bg-neutral-600 rounded-full flex items-center justify-center"
                    >
                      <span class="text-xs font-bold text-gray-900 dark:text-white">
                        {{ player.teamName?.charAt(0) || 'T' }}
                      </span>
                    </div>
                  </NuxtLink>
                  <NuxtLink
                    :to="`/players/${player.id}?tab=stats`"
                    class="font-sans font-bold text-base leading-tight tracking-normal text-gray-900 dark:text-white underline hover:text-gray-600 dark:hover:text-neutral-300 transition-colors cursor-pointer"
                  >
                    {{ player.name }}
                  </NuxtLink>
                </div>
              </td>
              <td
                class="font-sans font-normal text-base leading-tight tracking-normal px-2 pt-[1px] pb-[2px] text-right text-gray-900 dark:text-white w-20"
                :class="{ 'pt-[7px]': index === 0 }"
              >
                {{
                  selectedMetric === 'Hitting'
                    ? Number(player.OPS).toFixed(3)
                    : Number(player.FIP).toFixed(2)
                }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Loading Overlay -->
      <div
        v-if="isLoading"
        class="absolute inset-0 backdrop-blur-sm flex items-center justify-center z-1"
      >
        <div class="flex items-center gap-3">
          <div
            class="animate-spin rounded-full h-5 w-5 border-2 border-gray-900 dark:border-white border-t-transparent"
          ></div>
          <span class="text-gray-900 dark:text-white font-medium">Loading trending players...</span>
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->

    <!-- Empty State -->
    <div v-if="!isLoading && players.length === 0" class="text-center py-12">
      <div class="text-neutral-400">
        <div class="text-4xl mb-4">⚾</div>
        <h3 class="text-lg font-semibold mb-2">No Players Found</h3>
        <p class="text-sm">Try adjusting your filters or check back later.</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useQuery } from '@tanstack/vue-query';

// Props
interface Props {
  limit?: number;
}

const props = withDefaults(defineProps<Props>(), {
  limit: 15,
});

// Reactive state
const selectedSport = ref('Baseball');
const selectedMetric = ref('Hitting');
const tableContainer = ref<HTMLElement | null>(null);

// Available options
const sports = ['Baseball', 'Softball'];
const metrics = ['Hitting', 'Pitching'];

// Computed property for the metric name based on sport and metric
const metricDisplayName = computed(() => {
  if (selectedMetric.value === 'Hitting') {
    return 'OPS';
  } else {
    return 'FIP';
  }
});

// No mock data - only real data from API

// Function to scroll table to top with animation
const scrollToTop = () => {
  if (tableContainer.value) {
    tableContainer.value.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

// Fetch trending players data using vue-query for caching
const {
  data: queryData,
  isLoading: queryLoading,
  error: queryError,
} = useQuery({
  queryKey: ['trending-players', selectedSport, selectedMetric, props.limit],
  queryFn: async () => {
    const response = await $fetch('/api/trending-players', {
      query: {
        sport: selectedSport.value,
        metric: selectedMetric.value,
        limit: props.limit,
      },
    });
    return response?.data || [];
  },
  staleTime: 5 * 60 * 1000, // 5 minutes cache
});

// Computed for players data
const players = computed(() => queryData.value || []);
const isLoading = computed(() => queryLoading.value);

// Watch for filter changes to scroll to top
watch([selectedSport, selectedMetric], () => {
  scrollToTop();
});

// Handle team logo image errors
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.style.display = 'none';
  const parent = img.parentElement;
  if (parent) {
    parent.innerHTML = `
      <div class="w-full h-full bg-neutral-600 rounded-full flex items-center justify-center">
        <span class="text-xs font-bold text-white">${img.alt?.charAt(0) || 'T'}</span>
      </div>
    `;
  }
};
</script>
